import { CSSProperties, FC, useMemo } from "react";
import clsx from "clsx";
import { Recipe } from "@forms/schema";
import Image, { IImageProps } from "apps/website/components/base/Image/Image";
import Icon from "apps/website/components/base/Icon/Icon";
import { IconSvg } from "apps/website/components/base/Icon/Icon.map";

const PACKAGING_BASE_PATH = "/images/features/product-overview/packaging";
const SHREDDED_RECIPE_PREFIX = "SHREDDED_";
// The original collage was designed around a "full" 4-pack lineup, so 1-4
// recipes can keep that exact composition and only 5/6 need extra fitting logic.
const BASELINE_LINEUP_RECIPE_COUNT = 4;
// Each recipe image fills this stage, then gets shifted left/right by percentage.
// A 50% stage is what gives the original collage its card size in the white panel.
const LINEUP_STAGE_WIDTH_PERCENT = 50;
// Each neighbouring card moves by 35% of the stage width, which creates the
// familiar overlap rhythm from the original collage.
const LINEUP_POSITION_STEP_PERCENT = 35;
// Alternate cards tilt slightly to avoid a perfectly rigid row.
const ROTATED_RECIPE_ANGLE_DEGREES = -3;

interface IRecipeImagery {
  image: IImageProps;
  icon: IconSvg;
}

const RECIPE_IMAGE_MAP: Record<Recipe, IRecipeImagery> = {
  BAAA: { image: { src: `${PACKAGING_BASE_PATH}/baaa_2025.webp`, width: 1000, height: 1000 }, icon: "sheep" },
  CLUCK: { image: { src: `${PACKAGING_BASE_PATH}/cluck_2025.webp`, width: 1000, height: 1000 }, icon: "chicken" },
  SHREDDED_CLUCK: { image: { src: `${PACKAGING_BASE_PATH}/shredded-cluck.webp`, width: 1000, height: 1000 }, icon: "chicken" },
  GOBBLE: { image: { src: `${PACKAGING_BASE_PATH}/gobble_2026.webp`, width: 1000, height: 1000 }, icon: "turkey" },
  SHREDDED_GOBBLE: { image: { src: `${PACKAGING_BASE_PATH}/shredded-gobble.webp`, width: 1000, height: 1000 }, icon: "turkey" },
  MOOO: { image: { src: `${PACKAGING_BASE_PATH}/mooo_2026.webp`, width: 1000, height: 1000 }, icon: "cow" },
  OINK: { image: { src: `${PACKAGING_BASE_PATH}/oink_2025.webp`, width: 1000, height: 1000 }, icon: "pig" },
  QUACK: { image: { src: `${PACKAGING_BASE_PATH}/quack_2025.webp`, width: 1000, height: 1000 }, icon: "duck" },
  SPLASH: { image: { src: `${PACKAGING_BASE_PATH}/splash_2025.webp`, width: 1000, height: 1000 }, icon: "fish" },
  SWISH: { image: { src: `${PACKAGING_BASE_PATH}/swish_2026.webp`, width: 1000, height: 1000 }, icon: "fish" },
};

const isShreddedRecipe = (recipe: Recipe): boolean => recipe.startsWith(SHREDDED_RECIPE_PREFIX);

const getLineupStageStyle = (numberOfRecipes: number): CSSProperties => {
  if (numberOfRecipes <= BASELINE_LINEUP_RECIPE_COUNT) {
    return { width: `${LINEUP_STAGE_WIDTH_PERCENT}%` };
  }
  // For 5/6 cards, keep the same overlap pattern but narrow the stage
  // so the full lineup stays about as wide as the original 4-card collage.
  const baselineSpreadPercent = LINEUP_STAGE_WIDTH_PERCENT
    * (1 + (((BASELINE_LINEUP_RECIPE_COUNT - 1) * LINEUP_POSITION_STEP_PERCENT) / 100));
  const overlapFactor = 1 + (((numberOfRecipes - 1) * LINEUP_POSITION_STEP_PERCENT) / 100);
  return { width: `${baselineSpreadPercent / overlapFactor}%` };
};

// 1 -> 0
// 2 -> 17.5, -17.5
// 3 -> 35, 0, -35
// 4 -> 52.5, 17.5, -17.5, -52.5
// 5 -> 70, 35, 0, -35, -70
// 6 -> 87.5, 52.5, 17.5, -17.5, -52.5, -87.5
const getPositionPercentage = (numberOfRecipes: number, index: number): number => (
  (((numberOfRecipes - 1) / 2) - index) * LINEUP_POSITION_STEP_PERCENT
);

const getAlternateRecipeRotation = (numberOfRecipes: number, index: number): string => {
  const visualIndex = numberOfRecipes - index - 1;
  return visualIndex % 2 === 0
    ? `${ROTATED_RECIPE_ANGLE_DEGREES}deg`
    : "0deg";
};

export interface IRecipeLineupProps {
  recipes: Recipe[];
  className?: string;
}

export const RecipeLineup: FC<IRecipeLineupProps> = ({ recipes, className }) => {
  const reversed = useMemo(() => recipes.slice().reverse(), [ recipes ]);

  if (recipes.length === 0) { return null; }

  return (
    <div className={clsx("flex justify-center pt-8 bg-white", className)}>
      <div className="relative aspect-square" style={getLineupStageStyle(recipes.length)}>
        { reversed.map((recipe, index) => {
          const imagery = RECIPE_IMAGE_MAP[recipe];
          if (!imagery) { return null; }
          return (
            <div
              key={`${recipe}-${index}`}
              className="absolute h-full w-full"
              style={{
                transform: `translateX(${getPositionPercentage(recipes.length, index)}%)`,
                zIndex: index + 1,
              }}
            >
              { !isShreddedRecipe(recipe) && (
                <Icon
                  icon={imagery.icon}
                  className="absolute -top-6 right-4 -translate-x-6 md:-top-4 md:right-8 md:-translate-x-10 lg:-top-6 lg:right-4 lg:-translate-x-6 xl:-top-4 xl:right-8"
                />
              ) }
              <div style={{
                transform: `rotate(${getAlternateRecipeRotation(recipes.length, index)})`,
                transformOrigin: "bottom center",
              }}>
                <Image image={imagery.image} alt={recipe} />
              </div>
            </div>
          );
        }) }
      </div>
    </div>
  );
};
